// src/utils/imagemTemplate.js — prepara a imagem do cabeçalho do template.
//
// Out/26. Nasceu de um defeito de campo: a pessoa clicava em "Enviar
// imagem", escolhia a foto, e NADA acontecia — sem imagem e sem erro. A
// causa imediata era o `handleImage` do TemplateBuilder engolir a exceção
// (try/finally sem catch), mas consertar só isso trocaria "nada acontece"
// por "erro na tela": a foto continuaria não subindo. Este arquivo trata as
// razões pelas quais ela não subia.
//
// ─── Por que a foto do celular não passava ─────────────────────────────
// Foto de celular hoje tem 3 a 8 MB. Três paredes no caminho:
//
//   1. O Nginx na frente do backend tem `client_max_body_size` de 1 MB
//      por padrão. Acima disso ele devolve 413 ANTES de a requisição
//      chegar no FastAPI — o backend nem fica sabendo. O botão prometia
//      5 MB, que é o limite da META, não o do proxy.
//   2. A Meta recusa acima de 5 MB.
//   3. Foto de iPhone costuma ser .heic, que nem a Meta nem o backend
//      aceitam.
//
// Reduzir a imagem no NAVEGADOR resolve (1) e (2) de uma vez e ainda faz
// o upload voar no 4G. Para (3) não há conserto possível aqui — o
// navegador não decodifica HEIC —, então o que resta é dizer isso com
// todas as letras e ensinar a saída.
//
// ─── Por que não comprimir sempre ──────────────────────────────────────
// Recomprimir um PNG de 120 KB que já está ótimo só degrada, e converter
// pra JPEG achata transparência. Então a regra é: mexe só quando precisa
// (passou do teto de bytes ou de dimensão) e AVISA que mexeu. Imagem que
// já cabe sobe intacta.

// Teto da Meta pro cabeçalho de template.
export const MAX_META_BYTES = 5 * 1024 * 1024;

// Acima disto vale a pena reduzir. Escolhido abaixo do 1 MB que é o
// default do `client_max_body_size` do Nginx, com folga pro overhead do
// multipart — é o número que decide se a foto passa ou toma 413.
export const LIMITE_OTIMIZAR_BYTES = 700 * 1024;

// Maior lado depois de reduzir. O cabeçalho do WhatsApp é exibido em
// poucas centenas de pixels; 1600 dá margem pra tela retina sem carregar
// uma foto de 12 megapixels que ninguém vai ver inteira.
export const MAX_LADO_PX = 1600;

const QUALIDADE_JPEG = 0.82;

const TIPOS_ACEITOS = ['image/jpeg', 'image/png'];

export function formatarBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/** Extensão em minúsculas, com ponto. '' quando não dá pra saber. */
function extensaoDe(nome) {
  const m = /\.[a-z0-9]+$/i.exec(nome || '');
  return m ? m[0].toLowerCase() : '';
}

/** Recusa o que nem adianta tentar subir. Devolve a frase do erro, ou
 *  null quando está tudo bem. A checagem é aqui, e não só no backend,
 *  porque a resposta imediata evita a pessoa esperar o upload de 6 MB
 *  pra descobrir que o formato não serve. */
export function motivoParaRecusar(file) {
  const ext = extensaoDe(file?.name);
  const tipo = (file?.type || '').toLowerCase();

  if (ext === '.heic' || ext === '.heif' || tipo.includes('heic') || tipo.includes('heif')) {
    return 'Foto no formato HEIC (padrão do iPhone). A Meta só aceita JPG '
      + 'ou PNG. No iPhone: Ajustes → Câmera → Formatos → "Mais Compatível", '
      + 'ou compartilhe a foto por e-mail para convertê-la em JPG.';
  }
  if (tipo && !TIPOS_ACEITOS.includes(tipo)) {
    return `Formato não aceito pela Meta (${tipo}). Use JPG ou PNG.`;
  }
  if (!tipo && !['.jpg', '.jpeg', '.png'].includes(ext)) {
    return 'Formato não aceito pela Meta. Use JPG ou PNG.';
  }
  if (file.size > MAX_META_BYTES) {
    return `A imagem tem ${formatarBytes(file.size)} e o limite da Meta é 5 MB. `
      + 'Reduza a foto antes de enviar.';
  }
  return null;
}

/** Desenha o arquivo num canvas reduzido e devolve um File novo.
 *  Rejeita quando o navegador não consegue decodificar a imagem — o que
 *  acontece com HEIC e com arquivo corrompido. */
function reduzir(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      const maior = Math.max(img.naturalWidth, img.naturalHeight);
      const escala = maior > MAX_LADO_PX ? MAX_LADO_PX / maior : 1;
      const largura = Math.max(1, Math.round(img.naturalWidth * escala));
      const altura = Math.max(1, Math.round(img.naturalHeight * escala));

      const canvas = document.createElement('canvas');
      canvas.width = largura;
      canvas.height = altura;
      const ctx = canvas.getContext('2d');
      // Fundo branco antes de desenhar: PNG com transparência vira JPEG
      // aqui, e sem isto o transparente sairia PRETO. O WhatsApp compõe o
      // cabeçalho sobre a bolha de qualquer forma, então branco é o que
      // mais se aproxima do que a pessoa viu ao escolher.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, largura, altura);
      ctx.drawImage(img, 0, 0, largura, altura);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Não foi possível processar a imagem.'));
            return;
          }
          // Nome com .jpg porque os BYTES agora são JPEG, e o backend
          // valida o formato pela extensão do nome. Mandar bytes JPEG num
          // arquivo chamado .png passaria na validação e guardaria um
          // arquivo mentindo sobre o próprio conteúdo.
          const base = (file.name || 'imagem').replace(/\.[^.]+$/, '');
          resolve(new File([blob], `${base}.jpg`, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        QUALIDADE_JPEG,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('O navegador não conseguiu abrir esta imagem.'));
    };

    img.src = url;
  });
}

/**
 * Deixa o arquivo pronto pro upload.
 *
 * Devolve `{ file, otimizada, deBytes, paraBytes }`. `otimizada` diz se o
 * arquivo foi mexido — a tela usa isso pra contar o que aconteceu, porque
 * trocar o arquivo da pessoa em silêncio é o tipo de gentileza que vira
 * reclamação ("a imagem ficou pior e ninguém avisou").
 *
 * Se a redução falhar, devolve o arquivo ORIGINAL em vez de propagar o
 * erro: pode ser que ele passe assim mesmo, e falhar no upload dá uma
 * mensagem melhor do que falhar no canvas.
 */
export async function prepararImagem(file) {
  const deBytes = file.size;
  const precisa = deBytes > LIMITE_OTIMIZAR_BYTES;
  if (!precisa) {
    return { file, otimizada: false, deBytes, paraBytes: deBytes };
  }
  try {
    const menor = await reduzir(file);
    // Recomprimir nem sempre encolhe (imagem pequena já muito comprimida).
    // Se cresceu, fica com o original.
    if (menor.size >= deBytes) {
      return { file, otimizada: false, deBytes, paraBytes: deBytes };
    }
    return { file: menor, otimizada: true, deBytes, paraBytes: menor.size };
  } catch {
    return { file, otimizada: false, deBytes, paraBytes: deBytes };
  }
}

/** Erro do upload → frase que explica o que fazer.
 *
 *  O 413 merece tratamento próprio: ele vem do Nginx, não do backend, e o
 *  corpo é uma página HTML — sem isto a pessoa veria um pedaço de HTML na
 *  tela, ou nada. */
export function mensagemDeFalhaNoUpload(err) {
  const status = err?.response?.status;
  if (status === 413) {
    return 'O servidor recusou a imagem por tamanho. Tente uma foto menor — '
      + 'se o problema continuar, avise o suporte (limite de upload do servidor).';
  }
  const detalhe = err?.response?.data?.detail;
  if (typeof detalhe === 'string' && detalhe.trim()) return detalhe;
  if (status === 401 || status === 403) {
    return 'Sua sessão expirou. Entre de novo e tente outra vez.';
  }
  if (!err?.response) {
    return 'Sem conexão com o servidor. Verifique a internet e tente de novo.';
  }
  return 'Não foi possível enviar a imagem. Tente de novo.';
}
